import { cn } from '@cyphex/ui';
import type { LucideIcon } from 'lucide-react';

export interface CallActionButtonProps {
  label: string;
  icon: LucideIcon;
  tone: 'accept' | 'decline';
  onClick: () => void;
  disabled?: boolean;
}

/**
 * The green Accept / red Decline pill from the reference popup: a translucent
 * circular glyph well followed by the label, inside the standard xl pill button.
 */
export function CallActionButton({
  label, icon: Icon, tone, onClick, disabled = false,
}: CallActionButtonProps) {
  /* ROUND, WITH THE WORD UNDERNEATH - the shape every phone has taught people
     to answer. The label is the accessible name; the visible caption repeats it
     for sight and is hidden from screen readers so it is not read twice. */
  return (
    <span className="flex flex-col items-center gap-2">
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'flex h-14 w-14 items-center justify-center rounded-pill shadow-lg transition-transform duration-fast',
          'hover:scale-105 active:scale-95 disabled:pointer-events-none disabled:opacity-50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
          tone === 'accept'
            ? 'bg-success text-success-foreground shadow-success/30'
            : 'bg-destructive text-destructive-foreground shadow-destructive/30',
        )}
      >
        <Icon size={24} strokeWidth={2.25} aria-hidden />
      </button>
      <span aria-hidden className="text-sm font-medium text-muted-foreground">
        {label}
      </span>
    </span>
  );
}
